import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  Trash2,
  Edit3,
  X,
  Play,
  Calendar,
  FileText,
  Clock,
  Save,
  ArrowRight,
  ListTodo,
  Plus,
  User,
  Users,
  Tag,
  Link,
  HelpCircle,
} from 'lucide-react';
import { getDrafts, updateDraft, approveDraft, rejectDraft } from '../api/draft.service.js';
import { getProjects } from '../api/project.service.js';
import { getModules } from '../api/module.service.js';
import { getUserDirectory } from '../api/user.service.js';
import { getTasks } from '../api/task.service.js';
import { TASK_CATEGORIES } from '../constants/taskCategories.js';
import useUIStore from '../store/useUIStore.js';
import useAuthStore from '../store/useAuthStore.js';
import { getApiErrorMessage } from '../lib/apiErrors.js';
import { formatTicketDate } from './tickets/ticketUi.js';

export default function DraftReviewModal() {
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user?.id);
  const { addToast } = useUIStore();
  const [editingDraftId, setEditingDraftId] = useState(null);

  // Edit Form states
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPriority, setEditPriority] = useState('MEDIUM');
  const [editStatus, setEditStatus] = useState('TODO');
  const [editDeadline, setEditDeadline] = useState('');
  const [editModuleId, setEditModuleId] = useState('');
  const [editAssigneeId, setEditAssigneeId] = useState('');
  const [editReviewerId, setEditReviewerId] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editRemark, setEditRemark] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editSubTasks, setEditSubTasks] = useState([]);
  const [editNote, setEditNote] = useState('');
  const [editIsCompleted, setEditIsCompleted] = useState(false);
  const [editTaskId, setEditTaskId] = useState('');

  // Fetch pending drafts
  const { data: drafts = [], isLoading } = useQuery({
    queryKey: ['pending-drafts', userId],
    enabled: Boolean(userId),
    queryFn: async ({ signal }) => {
      const { data } = await getDrafts({ signal });
      return data.data || [];
    },
  });

  // Query database-related lists for preview and editing options
  const { data: projects = [] } = useQuery({
    queryKey: ['draft-projects'],
    queryFn: async () => {
      const { data } = await getProjects();
      return data.data || [];
    },
    staleTime: 60000,
  });

  const { data: modules = [] } = useQuery({
    queryKey: ['draft-modules'],
    queryFn: async () => {
      const { data } = await getModules();
      return data.data || [];
    },
    staleTime: 60000,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['draft-users'],
    queryFn: async () => {
      const { data } = await getUserDirectory();
      return data.data || [];
    },
    staleTime: 60000,
  });

  const { data: tasks = [] } = useQuery({
    queryKey: ['draft-tasks'],
    queryFn: async () => {
      const { data } = await getTasks();
      return data.data || [];
    },
    staleTime: 60000,
  });

  const approveMutation = useMutation({
    mutationFn: (id) => approveDraft(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['pending-drafts'] });
      // Invalidate tasks/todos to reflect the newly executed action
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['project-summary-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['todos'] });
      addToast({ type: 'success', message: 'Draft action approved and executed successfully!' });
    },
    onError: (error) => {
      addToast({
        type: 'error',
        message: getApiErrorMessage(error, 'Failed to approve draft action.'),
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (id) => rejectDraft(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-drafts'] });
      addToast({ type: 'success', message: 'Draft action rejected and discarded.' });
    },
    onError: (error) => {
      addToast({
        type: 'error',
        message: getApiErrorMessage(error, 'Failed to discard draft action.'),
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateDraft(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-drafts'] });
      setEditingDraftId(null);
      addToast({ type: 'success', message: 'Draft action updated successfully.' });
    },
    onError: (error) => {
      addToast({
        type: 'error',
        message: getApiErrorMessage(error, 'Failed to update draft action.'),
      });
    },
  });

  if (isLoading || drafts.length === 0) return null;

  const startEditing = (draft) => {
    setEditingDraftId(draft.id);
    const body = draft.jsonData?.body || {};

    if (draft.action.includes('TASK')) {
      setEditTitle(body.title || '');
      setEditDescription(body.description || '');
      setEditPriority(body.priority || 'MEDIUM');
      setEditStatus(body.status || 'TODO');
      setEditDeadline(body.deadlineAt ? new Date(body.deadlineAt).toISOString().split('T')[0] : '');
      setEditModuleId(body.moduleId || '');
      setEditAssigneeId(body.assigneeId || '');
      setEditReviewerId(body.reviewerId || '');
      setEditCategory(body.category || '');
      setEditRemark(body.remark || '');
      setEditTags((body.tags || []).join(', '));
      setEditSubTasks(
        (body.subTasks || []).map((st) => ({
          id: st.id || Math.random().toString(36).substring(2, 11),
          title: st.title || '',
          isCompleted: !!st.isCompleted,
          deadlineAt: st.deadlineAt ? new Date(st.deadlineAt).toISOString().split('T')[0] : '',
        }))
      );
    } else if (draft.action.includes('TODO')) {
      setEditNote(body.note || '');
      setEditIsCompleted(!!body.isCompleted);
      setEditTaskId(body.taskId || '');
    }
  };

  const handleSaveEdit = (draft) => {
    const originalBody = draft.jsonData?.body || {};
    let updatedBody = { ...originalBody };
    let metadata = draft.metadata;

    if (draft.action.includes('TASK')) {
      updatedBody.title = editTitle;
      updatedBody.description = editDescription;
      updatedBody.priority = editPriority;
      updatedBody.status = editStatus;
      updatedBody.deadlineAt = editDeadline || null;
      updatedBody.moduleId = editModuleId || null;
      updatedBody.assigneeId = editAssigneeId || null;
      updatedBody.reviewerId = editReviewerId || null;
      updatedBody.category = editCategory || null;
      updatedBody.remark = editRemark || null;
      updatedBody.tags = editTags
        ? editTags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
        : [];
      updatedBody.subTasks = editSubTasks.map((st) => ({
        id: st.id,
        title: st.title.trim(),
        isCompleted: st.isCompleted,
        deadlineAt: st.deadlineAt || null,
      }));

      if (draft.action === 'CREATE_TASK') {
        const selectedModule = modules.find((m) => m.id === editModuleId);
        const moduleName = selectedModule ? ` under module "${selectedModule.name}"` : '';
        metadata = `Create task: "${editTitle}"${moduleName}`;
      } else if (draft.action === 'UPDATE_TASK') {
        metadata = `Update task: "${editTitle}"`;
      }
    } else if (draft.action.includes('TODO')) {
      updatedBody.note = editNote;
      updatedBody.isCompleted = editIsCompleted;
      updatedBody.taskId = editTaskId || null;
      if (draft.action === 'CREATE_TODO') {
        metadata = `Create personal scratchpad todo: "${editNote}"`;
      } else if (draft.action === 'UPDATE_TODO') {
        metadata = `Update personal scratchpad todo: "${editNote}"`;
      }
    }

    updateMutation.mutate({
      id: draft.id,
      payload: {
        metadata,
        jsonData: {
          ...draft.jsonData,
          body: updatedBody,
        },
      },
    });
  };

  // Helpers to get human-readable details
  const getModuleName = (moduleId) => {
    const mod = modules.find((m) => m.id === moduleId);
    if (!mod) return 'Unknown Module';
    const proj = projects.find((p) => p.id === mod.projectId);
    return proj ? `${mod.name} (${proj.name})` : mod.name;
  };

  const getUserName = (userId) => {
    const usr = users.find((u) => u.id === userId);
    return usr ? `${usr.name} (${usr.email})` : 'Unassigned';
  };

  const getTaskTitle = (taskId) => {
    const tsk = tasks.find((t) => t.id === taskId);
    return tsk ? tsk.title : 'None';
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-9999 p-4 animate-in fade-in duration-200">
      <div className="bg-card w-full max-w-3xl rounded-4xl border border-border shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in duration-300">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border bg-linear-to-r from-amber-500/10 via-card to-card flex items-start gap-4">
          <div className="p-3 bg-amber-500/10 text-amber-500 rounded-2xl shrink-0 animate-pulse">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Pending API Actions Review</h2>
            <p className="text-sm text-muted-foreground mt-1">
              The following operations were requested via an external API token. For security,
              please review, edit if necessary, and approve them to complete execution.
            </p>
          </div>
        </div>

        {/* Draft List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {drafts.map((draft) => {
            const isEditing = editingDraftId === draft.id;
            const tokenPrefix = draft.apiTokenId
              ? `Token ID: ${draft.apiTokenId.substring(0, 8)}...`
              : 'External API';
            const actionLabel = draft.action.replace('_', ' ');

            return (
              <article
                key={draft.id}
                className={`rounded-2xl border transition-all duration-200 p-5 ${
                  isEditing
                    ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                    : 'border-border/60 bg-muted/10 hover:border-amber-500/30 hover:bg-amber-500/5'
                }`}
              >
                {/* Meta details row */}
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  <div className="flex items-center gap-2">
                    <span className="bg-amber-500/10 text-amber-500 px-2 py-0.5 rounded-md text-[10px]">
                      {actionLabel}
                    </span>
                    <span>•</span>
                    <span>{tokenPrefix}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    <span>
                      {formatTicketDate(draft.createdAt, { hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </div>
                </div>

                {isEditing ? (
                  /* Editing Mode Form */
                  <div className="space-y-4 mt-2">
                    {draft.action.includes('TASK') ? (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="md:col-span-2">
                            <label className="block text-xs font-bold text-muted-foreground mb-1 uppercase tracking-wider">
                              Task Title
                            </label>
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              className="w-full px-3 py-2 bg-background border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none text-sm"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs font-bold text-muted-foreground mb-1 uppercase tracking-wider">
                              Description
                            </label>
                            <textarea
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              className="w-full px-3 py-2 bg-background border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none min-h-20 text-sm resize-none"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-muted-foreground mb-1 uppercase tracking-wider">
                              Module (Project)
                            </label>
                            <select
                              value={editModuleId}
                              onChange={(e) => setEditModuleId(e.target.value)}
                              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none text-sm cursor-pointer"
                            >
                              <option value="">Select a Module...</option>
                              {modules.map((m) => {
                                const proj = projects.find((p) => p.id === m.projectId);
                                return (
                                  <option key={m.id} value={m.id}>
                                    {m.name} {proj ? `(${proj.name})` : ''}
                                  </option>
                                );
                              })}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-muted-foreground mb-1 uppercase tracking-wider">
                              Category
                            </label>
                            <select
                              value={editCategory}
                              onChange={(e) => setEditCategory(e.target.value)}
                              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none text-sm cursor-pointer"
                            >
                              <option value="">No Category</option>
                              {TASK_CATEGORIES.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-muted-foreground mb-1 uppercase tracking-wider">
                              Priority
                            </label>
                            <select
                              value={editPriority}
                              onChange={(e) => setEditPriority(e.target.value)}
                              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none text-sm cursor-pointer"
                            >
                              <option value="LOW">Low</option>
                              <option value="MEDIUM">Medium</option>
                              <option value="HIGH">High</option>
                              <option value="URGENT">Urgent</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-muted-foreground mb-1 uppercase tracking-wider">
                              Status
                            </label>
                            <select
                              value={editStatus}
                              onChange={(e) => setEditStatus(e.target.value)}
                              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none text-sm cursor-pointer"
                            >
                              <option value="BACKLOG">Backlog</option>
                              <option value="TODO">Ready</option>
                              <option value="IN_PROGRESS">In Progress</option>
                              <option value="REVIEW">Review</option>
                              <option value="DONE">Completed</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-muted-foreground mb-1 uppercase tracking-wider">
                              Assignee
                            </label>
                            <select
                              value={editAssigneeId}
                              onChange={(e) => setEditAssigneeId(e.target.value)}
                              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none text-sm cursor-pointer"
                            >
                              <option value="">Unassigned</option>
                              {users.map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.name} ({u.email})
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-muted-foreground mb-1 uppercase tracking-wider">
                              Reviewer
                            </label>
                            <select
                              value={editReviewerId}
                              onChange={(e) => setEditReviewerId(e.target.value)}
                              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none text-sm cursor-pointer"
                            >
                              <option value="">No Reviewer</option>
                              {users.map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.name} ({u.email})
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-muted-foreground mb-1 uppercase tracking-wider">
                              Deadline
                            </label>
                            <input
                              type="date"
                              value={editDeadline}
                              onChange={(e) => setEditDeadline(e.target.value)}
                              className="w-full px-3 py-2 bg-background border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none text-sm cursor-pointer"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-muted-foreground mb-1 uppercase tracking-wider">
                              Tags (comma separated)
                            </label>
                            <input
                              type="text"
                              value={editTags}
                              onChange={(e) => setEditTags(e.target.value)}
                              placeholder="e.g. feature, core, priority"
                              className="w-full px-3 py-2 bg-background border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none text-sm"
                            />
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-xs font-bold text-muted-foreground mb-1 uppercase tracking-wider">
                              Remark / Notes
                            </label>
                            <textarea
                              value={editRemark}
                              onChange={(e) => setEditRemark(e.target.value)}
                              placeholder="Developer remarks..."
                              className="w-full px-3 py-2 bg-background border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none min-h-16 text-sm resize-none"
                            />
                          </div>

                          {/* Subtasks Management section */}
                          <div className="md:col-span-2 pt-2">
                            <label className="text-xs font-bold text-muted-foreground mb-2 uppercase tracking-wider flex items-center gap-1.5">
                              <ListTodo className="w-4 h-4 text-primary" />
                              Sub-tasks Management ({editSubTasks.length})
                            </label>

                            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                              {editSubTasks.map((st, index) => (
                                <div
                                  key={st.id}
                                  className="flex items-center gap-2 bg-muted/25 p-2 rounded-xl border border-border/40"
                                >
                                  <input
                                    type="checkbox"
                                    checked={st.isCompleted}
                                    onChange={(e) => {
                                      const updated = [...editSubTasks];
                                      updated[index].isCompleted = e.target.checked;
                                      setEditSubTasks(updated);
                                    }}
                                    className="w-4 h-4 rounded border-input bg-background accent-primary cursor-pointer shrink-0"
                                  />
                                  <input
                                    type="text"
                                    value={st.title}
                                    placeholder="Sub-task title..."
                                    onChange={(e) => {
                                      const updated = [...editSubTasks];
                                      updated[index].title = e.target.value;
                                      setEditSubTasks(updated);
                                    }}
                                    className="flex-1 min-w-0 px-2.5 py-1 bg-background border border-input rounded-lg focus:ring-2 focus:ring-ring outline-none text-xs"
                                  />
                                  <input
                                    type="date"
                                    value={st.deadlineAt}
                                    onChange={(e) => {
                                      const updated = [...editSubTasks];
                                      updated[index].deadlineAt = e.target.value;
                                      setEditSubTasks(updated);
                                    }}
                                    className="w-28 px-1.5 py-1 bg-background border border-input rounded-lg focus:ring-2 focus:ring-ring outline-none text-[11px] cursor-pointer shrink-0 text-center"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditSubTasks(
                                        editSubTasks.filter((_, idx) => idx !== index)
                                      );
                                    }}
                                    className="p-1 hover:bg-destructive/10 hover:text-destructive text-muted-foreground rounded-lg transition-colors cursor-pointer shrink-0"
                                    title="Delete subtask"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}

                              {editSubTasks.length === 0 && (
                                <p className="text-xs text-muted-foreground italic text-center py-2 bg-muted/10 rounded-xl border border-dashed border-border/60">
                                  No sub-tasks defined for this task.
                                </p>
                              )}
                            </div>

                            <button
                              type="button"
                              onClick={() => {
                                setEditSubTasks([
                                  ...editSubTasks,
                                  {
                                    id: Math.random().toString(36).substring(2, 11),
                                    title: '',
                                    isCompleted: false,
                                    deadlineAt: '',
                                  },
                                ]);
                              }}
                              className="mt-2 px-3 py-1.5 border border-dashed border-border hover:border-primary/50 text-muted-foreground hover:text-primary rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer w-full justify-center bg-muted/5"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              Add Sub-task
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-bold text-muted-foreground mb-1 uppercase tracking-wider">
                              Todo Note
                            </label>
                            <input
                              type="text"
                              value={editNote}
                              onChange={(e) => setEditNote(e.target.value)}
                              className="w-full px-3 py-2 bg-background border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-muted-foreground mb-1 uppercase tracking-wider">
                              Link to Task
                            </label>
                            <select
                              value={editTaskId}
                              onChange={(e) => setEditTaskId(e.target.value)}
                              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl focus:ring-2 focus:ring-ring outline-none text-sm cursor-pointer"
                            >
                              <option value="">Not linked to any task</option>
                              {tasks.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.title}
                                </option>
                              ))}
                            </select>
                          </div>

                          {draft.action === 'UPDATE_TODO' && (
                            <div className="flex items-center gap-2 mt-2">
                              <input
                                type="checkbox"
                                id={`completed-${draft.id}`}
                                checked={editIsCompleted}
                                onChange={(e) => setEditIsCompleted(e.target.checked)}
                                className="w-4 h-4 rounded border-input bg-background accent-primary cursor-pointer"
                              />
                              <label
                                htmlFor={`completed-${draft.id}`}
                                className="text-sm font-semibold text-foreground cursor-pointer select-none"
                              >
                                Mark as Completed
                              </label>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Edit Form Buttons */}
                    <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
                      <button
                        type="button"
                        onClick={() => setEditingDraftId(null)}
                        className="px-3.5 py-1.5 border border-input rounded-xl text-xs font-semibold hover:bg-muted transition-colors cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={updateMutation.isPending}
                        onClick={() => handleSaveEdit(draft)}
                        className="px-3.5 py-1.5 bg-primary text-primary-foreground rounded-xl text-xs font-semibold hover:opacity-90 flex items-center gap-1 transition-all cursor-pointer disabled:opacity-50"
                      >
                        <Save className="w-3.5 h-3.5" />
                        Save Changes
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Standard Display Mode */
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-bold text-foreground leading-normal">
                          {draft.metadata}
                        </h4>
                        {/* Sub-details preview if available */}
                        {draft.jsonData?.body?.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1 mt-1 font-sans">
                            {draft.jsonData.body.description}
                          </p>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => startEditing(draft)}
                          className="p-2 border border-border/80 text-muted-foreground hover:text-primary hover:border-primary/30 rounded-xl transition-colors cursor-pointer"
                          title="Edit action properties"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          disabled={rejectMutation.isPending}
                          onClick={() => rejectMutation.mutate(draft.id)}
                          className="p-2 border border-border/80 text-muted-foreground hover:text-destructive hover:border-destructive/30 rounded-xl transition-colors cursor-pointer disabled:opacity-50"
                          title="Discard this action"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>

                        <div className="h-6 w-px bg-border/80 mx-1" />

                        <button
                          type="button"
                          disabled={approveMutation.isPending}
                          onClick={() => approveMutation.mutate(draft.id)}
                          className="px-4 py-2 bg-primary text-primary-foreground hover:opacity-95 active:scale-95 rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-all cursor-pointer disabled:opacity-50"
                        >
                          <Check className="w-4 h-4" />
                          Approve
                        </button>
                      </div>
                    </div>

                    {/* Detailed Metadata Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mt-2 pt-2 border-t border-border/20 text-xs">
                      {draft.action.includes('TASK') ? (
                        <>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <HelpCircle className="w-4 h-4 text-amber-500 shrink-0" />
                            <span>Module:</span>
                            <span className="font-semibold text-foreground">
                              {getModuleName(draft.jsonData?.body?.moduleId)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Tag className="w-4 h-4 text-purple-500 shrink-0" />
                            <span>Category:</span>
                            <span className="font-semibold text-foreground">
                              {TASK_CATEGORIES.find((c) => c.id === draft.jsonData?.body?.category)
                                ?.name || 'None'}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 text-muted-foreground">
                            <User className="w-4 h-4 text-blue-500 shrink-0" />
                            <span>Assignee:</span>
                            <span className="font-semibold text-foreground">
                              {getUserName(draft.jsonData?.body?.assigneeId)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Users className="w-4 h-4 text-teal-500 shrink-0" />
                            <span>Reviewer:</span>
                            <span className="font-semibold text-foreground">
                              {getUserName(draft.jsonData?.body?.reviewerId)}
                            </span>
                          </div>

                          {draft.jsonData?.body?.priority && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <span className="w-4 h-4 flex items-center justify-center font-bold text-amber-600">
                                !
                              </span>
                              <span>Priority:</span>
                              <span className="font-bold text-foreground">
                                {draft.jsonData.body.priority}
                              </span>
                            </div>
                          )}

                          {draft.jsonData?.body?.status && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <span className="w-4 h-4 flex items-center justify-center font-semibold text-emerald-500">
                                ✓
                              </span>
                              <span>Status:</span>
                              <span className="font-semibold text-foreground">
                                {draft.jsonData.body.status.replace('_', ' ')}
                              </span>
                            </div>
                          )}

                          {draft.jsonData?.body?.deadlineAt && (
                            <div className="flex items-center gap-2 text-muted-foreground md:col-span-2">
                              <Calendar className="w-4 h-4 text-red-400 shrink-0" />
                              <span>Deadline:</span>
                              <span className="font-semibold text-foreground">
                                {draft.jsonData.body.deadlineAt.split('T')[0]}
                              </span>
                            </div>
                          )}

                          {draft.jsonData?.body?.tags && draft.jsonData.body.tags.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5 md:col-span-2 mt-1">
                              <span className="text-muted-foreground mr-1">Tags:</span>
                              {draft.jsonData.body.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="px-2 py-0.5 bg-muted text-muted-foreground border border-border/80 rounded-md text-[10px] font-semibold"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {draft.jsonData?.body?.remark && (
                            <div className="md:col-span-2 bg-muted/20 p-2.5 rounded-xl border border-border/40 mt-1">
                              <span className="block text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">
                                Developer Notes / Remarks
                              </span>
                              <p className="text-xs text-foreground italic leading-relaxed">
                                {draft.jsonData.body.remark}
                              </p>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {draft.jsonData?.body?.taskId && (
                            <div className="flex items-center gap-2 text-muted-foreground md:col-span-2">
                              <Link className="w-4 h-4 text-blue-400 shrink-0" />
                              <span>Linked Task:</span>
                              <span className="font-semibold text-foreground">
                                {getTaskTitle(draft.jsonData.body.taskId)}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Subtasks view in standard display mode */}
                    {draft.jsonData?.body?.subTasks && draft.jsonData.body.subTasks.length > 0 && (
                      <div className="pl-4 py-1.5 border-l-2 border-border/60 bg-muted/5 rounded-r-lg mt-1">
                        <h5 className="text-xs font-bold text-muted-foreground mb-2 flex items-center gap-1.5 uppercase tracking-wider">
                          <ListTodo className="w-3.5 h-3.5 text-primary" />
                          Sub-tasks ({draft.jsonData.body.subTasks.length})
                        </h5>
                        <ul className="space-y-1.5">
                          {draft.jsonData.body.subTasks.map((st) => (
                            <li
                              key={st.id}
                              className="flex items-center gap-2.5 text-xs text-foreground"
                            >
                              <input
                                type="checkbox"
                                checked={!!st.isCompleted}
                                disabled
                                className="w-3.5 h-3.5 rounded border-input bg-background accent-primary cursor-not-allowed shrink-0"
                              />
                              <span
                                className={
                                  st.isCompleted ? 'line-through text-muted-foreground' : ''
                                }
                              >
                                {st.title}
                              </span>
                              {st.deadlineAt && (
                                <span className="text-[10px] text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded-md flex items-center gap-1 font-semibold ml-auto shrink-0">
                                  <Calendar className="w-3 h-3 text-muted-foreground" />
                                  {st.deadlineAt.split('T')[0]}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-muted/20 flex justify-between items-center text-xs text-muted-foreground">
          <span>
            {drafts.length} action{drafts.length === 1 ? '' : 's'} pending review
          </span>
          <div className="flex items-center gap-1">
            <span>ManageMate Safety Shield Active</span>
            <div className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" />
          </div>
        </div>
      </div>
    </div>
  );
}
